// Framework and third-party non-ui
import { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Helpers/utils/modules
import { useAppContext } from "../../contexts/AppContext";
import LocationsPage from "../../pages/Locations";
import LandingPage from "../../pages/Landing";

import { AppTheme, LayerSourceInfo, LocatorSourceInfo, Utils } from "nyc-component-lib";

import FeatureLayer from "@arcgis/core/layers/FeatureLayer";

import "@arcgis/core/assets/esri/themes/light/main.css";
import "@esri/calcite-components/dist/calcite/calcite.css";
import { getCookie } from "../../utils/readCookie";

import "./App.css";

interface AppProps {
  relUrl: string;
}

const App = ({ relUrl }: AppProps) => {
  const {
    updateLocationsFeatureLayer,
    appConfig,
    updateAppConfig,
    isActive,
    eventStatus,
    evacZones,
    alternateAppTitle,
    alternateBannerText,
    alternateDescriptionText,
    layoutDir,
    updateLayoutDir,
    isEmergency,
  } = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [currEvacZone, setCurrEvacZone] = useState<number | undefined>(undefined);

  const [lastLang, setLastLang] = useState(getCookie("googtrans"));
  const lastLangRef = useRef(lastLang);

  const appId = window.appConfig.appId || "error";

  const getAppTitle = (): string => {
    const appTitleObj = t(`app.info.appTitle.${appId}`, {
      returnObjects: true,
    });
    for (const [k, v] of Object.entries(appTitleObj)) {
      if (k === eventStatus) {
        return v;
      }
    }

    return t(`app.info.appTitle.${appId}.default`);
  };

  useEffect(() => {
    // Set top level title
    const headElement = document.getElementsByTagName("head");
    const titleElement = document.createElement("title");
    titleElement.innerText = getAppTitle();
    headElement[0].appendChild(titleElement);

    let intervalId: ReturnType<typeof setTimeout> | undefined = undefined;

    updateAppConfig(window.appConfig);

    const locationsFeatureLayer = new FeatureLayer({
      url: window.activeMapConfig.loc_layer_url,
    });
    updateLocationsFeatureLayer(locationsFeatureLayer);

    if (window.appConfig.useGoogleTranslate) {
      window.googleTranslateElementInit();
      const googtrans = getCookie("googtrans");
      let toLang = null;
      if (googtrans) {
        const langs = googtrans.split("/");
        toLang = langs[2]; // e.g., "es"
      }
      updateLayoutDir(toLang || "en");

      // check for language change every x seconds - useful for when swithing between ltr and rtl languages
      intervalId = setInterval(() => {
        const currentLang = getCookie("googtrans");
        let newLang = null;
        if (currentLang) {
          const langs = currentLang.split("/");
          newLang = langs[2]; // e.g., "es"
        } else {
          newLang = "en";
        }

        if (newLang !== lastLangRef.current) {
          // console.log('Language changed from', lastLangRef.current, 'to', newLang);
          lastLangRef.current = newLang;
          setLastLang(newLang);
          updateLayoutDir(newLang);
        }
      }, 2000);
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      // Check if a parameter exists
      const hasParam = urlParams.has("i18n");
      // Get the value of a parameter
      let paramValue = "en";
      if (hasParam) paramValue = urlParams.get("i18n") || "en";

      i18n.language = paramValue;

      Utils.setLanguage(i18n.language);
      i18n.changeLanguage(i18n.language);
      updateLayoutDir(i18n.language);
    }
    // we only want this to happen first load
    // eslint-disable-next-line react-hooks/exhaustive-deps

    return () => clearInterval(intervalId); // Cleanup on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const select = document.querySelector(".goog-te-combo") as HTMLSelectElement;
    if (select) {
      select.dispatchEvent(new Event("change"));
    }
    if (isActive) {
      if (location.pathname.includes("deactivated")) {
        navigate("/");
      }
    } else {
      if (!location.pathname.includes("deactivated")) {
        navigate("deactivated");
      }
    }
  }, [isActive, location, navigate]);

  const viewCommonProps = {
    appTitle: getAppTitle(),
    sourceInfos: [
      {
        type: "layer",
        url: window.activeMapConfig.loc_layer_url,
        viewURL: window.activeMapConfig.loc_layer_view_url,
        fieldName: window.activeMapConfig?.search.searchLocFieldName,
      } as LayerSourceInfo,
      {
        type: "locator",
        url: window.activeMapConfig?.search.geoCodeURL,
      } as LocatorSourceInfo,
    ],
    eventState: isEmergency ? "active" : eventStatus,
    evacZones: evacZones,
    alternateAppTitle: alternateAppTitle,
    alternateBannerText: alternateBannerText,
    alternateDescriptionText: alternateDescriptionText,
    emergency: isEmergency,
  };

  if (appConfig) {
    const fieldLabels = t(`app.fieldLabels.${appConfig.appId}`, {
      returnObjects: true,
    });

    return (
      <div
        data-testid="App"
        role="main"
        aria-label={t(`app.info.ariaAppTitle.${appId}`)}
        style={{ height: "inherit" }}
        dir={layoutDir}
      >
        <AppTheme />
        <Routes>
          <Route
            path="/"
            element={
              <LandingPage
                {...viewCommonProps}
                deactivated={false}
                relUrl={relUrl}
                setCurrEvacZone={(zone) => {
                  setCurrEvacZone(zone);
                }}
              />
            }
          />
          <Route
            path="/deactivated"
            element={
              <LandingPage
                {...viewCommonProps}
                deactivated={true}
                relUrl={relUrl}
                setCurrEvacZone={(zone) => {
                  setCurrEvacZone(zone);
                }}
              />
            }
          />
          <Route
            path="/locations"
            element={
              <LocationsPage
                {...viewCommonProps}
                fieldLabels={fieldLabels}
                layoutDir={layoutDir}
                currEvacZone={currEvacZone}
              ></LocationsPage>
            }
          />
          {/* React Router v6 does not support optional params, recommendation is to duplicate path */}
          <Route
            path="/locations/:locationId"
            element={
              <LocationsPage
                {...viewCommonProps}
                fieldLabels={fieldLabels}
                layoutDir={layoutDir}
                currEvacZone={currEvacZone}
              ></LocationsPage>
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    );
  } else {
    return <div></div>;
  }
};

export default App;
